/* ===========================================================================
 * TimeframeSelectorMolecule.tsx
 *
 * Lazy-loading Timeframe hierarchy built on PrimeReact <TreeSelect>.
 * · Supports virtual (RRULE-computed) periods alongside materialized ones
 * · On-demand materialization when user selects a virtual period
 * · Each node carries its full Sphere in .data (or null if virtual)
 * · DEBUG logs are left intact – remove only if you *ask* for it
 * · Node stays expanded once children are loaded
 * · Props interface is exported so downstream code sees onChange
 * ==========================================================================*/

import React, { useEffect, useState, useCallback } from 'react';

import {
  TreeSelect,
  TreeSelectChangeEvent,
  TreeSelectEventNodeEvent
} from 'primereact/treeselect';
import { ProgressSpinner } from 'primereact/progressspinner';

import { Sphere } from 'types/global';
import {
  fetchTimeframesData,
  fetchVirtualPeriods,
  materializePeriod,
  VirtualPeriod
} from '@center-platform/spheres/identity';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useTranslation } from 'localization';
import './TimeframeSelectorMolecule.scss';

/* --------------------------------------------------------------------------
 * PrimeReact does not export its TreeNode type.
 * We recreate only the bits we actually need.
 * ------------------------------------------------------------------------ */
interface BaseNode {
  key      : string | number;
  label?   : string;
  data?    : any;
  children?: BaseNode[];
  leaf?    : boolean;
  loading? : boolean;
  expanded?: boolean;
  className?: string;
}

/** TreeSelect node that carries a Sphere in .data (or null for virtual) */
interface TFNode extends BaseNode {
  data: Sphere | null;
  children?: TFNode[];
  /** True if this period has not been materialized yet */
  isVirtual?: boolean;
  /** Root timeframe sphere ID (for virtual period API calls) */
  rootTimeframeId?: string;
  /** Original virtual period data */
  virtualPeriod?: VirtualPeriod;
  /** Parent's timeframe key (for re-fetching after materialization) */
  parentTimeframeKey?: string;
}

/** Maps a parent level to its child level */
const NEXT_LEVEL: Record<string, string> = {
  year: 'quarter',
  quarter: 'month',
  month: 'week',
};

/** Build a Sphere-compatible object from VirtualPeriod data */
const buildSphereFromVP = (vp: VirtualPeriod, rootTFId?: string): Sphere => ({
  id: vp.materializedId,
  label: vp.name,
  properties: {
    name: vp.name,
    timeframeKey: vp.key,
    startDate: vp.startDate,
    endDate: vp.endDate,
    hasChildren: vp.hasChildren,
    rootTimeframeId: rootTFId,
    virtualLevel: vp.level,
  }
} as unknown as Sphere);

/* --------------------------------------------------------------------------
 * Public props (exported so TS consumers pick up onChange properly)
 * ------------------------------------------------------------------------ */
export interface TimeframeSelectorProps {
  organizationId: string;
  onChange?: (selected: Sphere | null) => void;
  label?: string;
}

/* ===========================================================================
 * COMPONENT
 * ==========================================================================*/
const TimeframeSelectorMolecule: React.FC<TimeframeSelectorProps> = ({
  organizationId,
  onChange,
  label = ''
}) => {
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;
  const [nodes,        setNodes]        = useState<TFNode[]>([]);
  const [loadingRoot,  setLoadingRoot]  = useState(true);
  const [selectionKey, setSelectionKey] = useState<string | null>(null);
  const [materializing, setMaterializing] = useState(false);
  const { t } = useTranslation();

  /* --- Sphere -> TreeSelect node (root timeframes only) ---------------- */
  const sphereToNode = (s: Sphere): TFNode => ({
    key  : (s as any).id,
    label: (s.properties as any).name ?? s.label,
    leaf : false,              // root TFs always have virtual children
    data : s,
    isVirtual: false,
  });

  /* --- VirtualPeriod -> TreeSelect node -------------------------------- */
  const virtualPeriodToNode = (
    vp: VirtualPeriod,
    rootTFId: string,
    parentKey?: string
  ): TFNode => ({
    key   : vp.key,
    label : vp.name,
    leaf  : !vp.hasChildren,
    data  : vp.materialized ? buildSphereFromVP(vp, rootTFId) : null,
    isVirtual         : !vp.materialized,
    rootTimeframeId   : rootTFId,
    virtualPeriod     : vp,
    parentTimeframeKey: parentKey,
    className         : vp.materialized ? undefined : 'virtual-period-node',
  });

  /* --- immutably replace a node ---------------------------------------- */
  const replaceNode = (
    list: TFNode[],
    key : string | number,
    fn  : (n: TFNode) => TFNode
  ): TFNode[] =>
    list.map(n =>
      n.key === key
        ? fn(n)
        : {
            ...n,
            children: n.children ? replaceNode(n.children, key, fn) : n.children
          }
    );

  /* --- find a node in the tree ----------------------------------------- */
  const findNode = (list: TFNode[], key: string | number): TFNode | null => {
    for (const n of list) {
      if (n.key === key) return n;
      const sub = n.children && findNode(n.children, key);
      if (sub) return sub;
    }
    return null;
  };

  /* --- initial root fetch ---------------------------------------------- */
  useEffect(() => {
    if (!graphClient) return;
    let cancelled = false;

    // Failures are logged by the try/catch inside, so nothing escapes this call.
    void (async () => {
      console.log('[TimeframeSelector] fetching root nodes');              // #
      try {
        const { spheres } =
          await fetchTimeframesData(graphClient, { limit: 100 });
        if (!cancelled) setNodes(spheres.map(sphereToNode));
      } catch (err) {
        console.error('[TimeframeSelector] root fetch failed', err);       // #
      } finally {
        if (!cancelled) setLoadingRoot(false);
      }
    })();

    return () => { cancelled = true; };
  }, [organizationId, graphClient]);

  /* --- lazy-load children ---------------------------------------------- */
  const onNodeExpand = useCallback(
    async (e: TreeSelectEventNodeEvent) => {
      if (!graphClient) return;
      const node = e.node as TFNode;
      console.log('[TimeframeSelector] expanding node:', node.key);         // #
      if (!node || node.children) return;   // already loaded

      /* mark node as loading */
      setNodes(prev =>
        replaceNode(prev, node.key, n => ({ ...n, loading: true }))
      );

      try {
        const rootTFId = node.rootTimeframeId || (node.data as any)?.id;
        const childLevel = node.virtualPeriod
          ? NEXT_LEVEL[node.virtualPeriod.level]
          : 'year';
        const parentKey = node.virtualPeriod?.key;

        if (rootTFId && childLevel) {
          const { periods } = await fetchVirtualPeriods(graphClient, {
            rootTimeframeId: rootTFId,
            level: childLevel as 'year' | 'quarter' | 'month' | 'week',
            parentKey,
          });

          const kids = periods.map(vp =>
            virtualPeriodToNode(vp, rootTFId, parentKey)
          );
          console.log(
            `[TimeframeSelector] fetched ${kids.length} virtual children for ${node.key}`
          );                                                               // #

          setNodes(prev =>
            replaceNode(prev, node.key, n => ({
              ...n,
              loading : false,
              leaf    : kids.length === 0,
              expanded: true,
              children: kids,
            }))
          );
        } else {
          // Fallback: use existing fetch for non-calendar timeframes
          const { spheres } = await fetchTimeframesData(graphClient, {
            parentKey: (node.data as any)?.properties?.timeframeKey,
            limit: 100,
          });

          const kids: TFNode[] = spheres.map(s => ({
            ...sphereToNode(s),
            rootTimeframeId: rootTFId,
          }));
          console.log(
            `[TimeframeSelector] fetched ${kids.length} children for ${node.key}`
          );                                                               // #

          setNodes(prev =>
            replaceNode(prev, node.key, n => ({
              ...n,
              loading : false,
              leaf    : kids.length === 0,
              expanded: true,
              children: kids,
            }))
          );
        }
      } catch (err) {
        console.error('[TimeframeSelector] child fetch failed', err);       // #
        setNodes(prev =>
          replaceNode(prev, node.key, n => ({ ...n, loading: false }))
        );
      }
    },
    [graphClient, organizationId]
  );

  // Early return AFTER all hooks (Rules of Hooks)
  if (!apiClients || !graphClient) {
    return null;
  }

  /* --- selection with transparent materialization ---------------------- */
  const onSelect = async (e: TreeSelectChangeEvent) => {
    const key = e.value as string | null;
    setSelectionKey(key);

    if (!onChange) return;
    if (!key) { onChange(null); return; }

    const node = findNode(nodes, key);
    if (!node) { onChange(null); return; }

    // Already materialized — pass sphere directly
    if (!node.isVirtual && node.data) {
      onChange(node.data);
      return;
    }

    // Virtual node — materialize transparently then pass sphere
    if (node.isVirtual && node.virtualPeriod && node.rootTimeframeId) {
      setMaterializing(true);
      try {
        console.log('[TimeframeSelector] materializing for:', node.virtualPeriod.key); // #

        await materializePeriod(graphClient, {
          rootTimeframeId: node.rootTimeframeId,
          targetDate: node.virtualPeriod.startDate,
          targetLevel: node.virtualPeriod.level as 'year' | 'quarter' | 'month' | 'week',
        });

        // Re-fetch virtual periods to get materializedId
        const { periods } = await fetchVirtualPeriods(graphClient, {
          rootTimeframeId: node.rootTimeframeId,
          level: node.virtualPeriod.level as 'year' | 'quarter' | 'month' | 'week',
          parentKey: node.parentTimeframeKey,
        });

        const updated = periods.find(vp => vp.key === node.virtualPeriod!.key);

        if (updated?.materializedId) {
          const sphere = buildSphereFromVP(updated, node.rootTimeframeId);

          // Update sibling nodes that became materialized
          setNodes(prev => {
            let result = prev;
            for (const vp of periods) {
              if (vp.materialized) {
                result = replaceNode(result, vp.key, n => ({
                  ...n,
                  isVirtual: false,
                  className: undefined,
                  data: buildSphereFromVP(vp, node.rootTimeframeId),
                  virtualPeriod: { ...vp },
                }));
              }
            }
            return result;
          });

          console.log('[TimeframeSelector] materialized, sphere:', updated.materializedId); // #
          onChange(sphere);
        } else {
          console.error('[TimeframeSelector] period not found after materialization'); // #
          onChange(null);
        }
      } catch (err) {
        console.error('[TimeframeSelector] materialization failed', err);   // #
        setSelectionKey(null);
        onChange(null);
      } finally {
        setMaterializing(false);
      }
    }
  };

  /* --- render ----------------------------------------------------------- */
  if (loadingRoot) return <ProgressSpinner style={{ width: '20px', height: '20px', margin: '10px 0 0 10px' }}
                    strokeWidth="4"
                    aria-label={t('periodSelector.loadingTimeframes')}/>;

  return (
    <div className="flex flex-column gap-2">
      {/* The hidden input is the control assistive technology reaches: it opens a tree popup, so it
          is a combobox (PrimeReact's default `listbox` is not allowed on an input). The chevron is
          a mouse-only affordance that is never focused, so it is hidden from the accessibility tree. */}
      <TreeSelect
        ariaLabel={label || t('periodSelector.timeframePickerLabel')}
        pt={{ hiddenInput: { role: 'combobox' }, trigger: { 'aria-hidden': true } }}
        value={selectionKey ?? undefined}
        onChange={onSelect}
        options={nodes}
        selectionMode="single"
        onNodeExpand={onNodeExpand}
        filter
        placeholder={t('periodSelector.AllTimeFrames')}
        className="w-full"
        panelClassName="timeframe-selector-panel"
        disabled={materializing}
      />
    </div>
  );
};

export default TimeframeSelectorMolecule;
